import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  message,
  Badge,
  Space,
  Select,
  Table
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newBlog, setNewBlog] = useState({
    id: null,
    title: "",
    content: "",
    status: "Draft", // Default to 'Draft'
    image: null,
    remove_image: false,
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [preview, setPreview] = useState(null);

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://agpipefitting.org.et/api/blogs");
      setBlogs(response.data);
      setFilteredBlogs(response.data);
    } catch (error) {
      message.error("Error fetching blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleEditBlog = (blog) => {
    setNewBlog({
      id: blog.id,
      title: blog.title,
      content: blog.content,
      status: blog.status,
      image: blog.image,
      remove_image: false,
    });
    setPreview(blog.image ? `https://agpipefitting.org.et/storage/${blog.image}` : null);
    setShowModal(true);
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm("Are you sure you want to delete this blog post?")) {
      try {
        await axios.delete(`https://agpipefitting.org.et/api/blogs/${blogId}`);
        setBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
        setFilteredBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
        message.success("Blog deleted successfully");
      } catch (error) {
        message.error("Error deleting blog");
      }
    }
  };

  const handleAddBlog = async () => {
    // Basic validation for title and content
    if (!newBlog.title || !newBlog.content) {
      message.error("Title and Content are required");
      return;
    }

    const formData = new FormData();
    formData.append("title", newBlog.title);
    formData.append("content", newBlog.content);
    formData.append("status", newBlog.status);

    // Handle image removal or addition
    if (newBlog.remove_image) {
      formData.append("remove_image", "1");
    } else if (newBlog.image instanceof File) {
      formData.append("image", newBlog.image);
    }

    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      let response;

      console.log("Sending data:", formData); // Log form data

      // Update existing blog
      if (newBlog.id) {
        response = await axios.put(
          `https://agpipefitting.org.et/api/blogs/${newBlog.id}`,
          formData,
          config
        );
        setBlogs((prev) => prev.map((blog) => (blog.id === newBlog.id ? response.data : blog)));
        message.success("Blog updated successfully");
      } else {
        // Create new blog
        response = await axios.post("https://agpipefitting.org.et/api/blogs", formData, config);
        setBlogs((prev) => [...prev, response.data]);
        message.success("Blog created successfully");
      }

      updateFilteredBlogs(response.data);
      handleCloseModal();
    } catch (error) {
      console.error("Error response:", error.response);
      message.error(error.response?.data?.message || "Error saving blog post");
    }
  };

  const updateFilteredBlogs = (updatedBlog) => {
    setFilteredBlogs((prev) => {
      const index = prev.findIndex((b) => b.id === updatedBlog.id);
      return index >= 0
        ? [...prev.slice(0, index), updatedBlog, ...prev.slice(index + 1)]
        : [...prev, updatedBlog];
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewBlog({ id: null, title: "", content: "", status: "Draft", remove_image: false });
    setPreview(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query.toLowerCase());
    setFilteredBlogs(blogs.filter((blog) => blog.title.toLowerCase().includes(query.toLowerCase())));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];

      if (!validTypes.includes(fileType)) {
        message.error("Invalid image format. Please upload a JPEG, PNG, JPG, or GIF image.");
        return;
      }

      setNewBlog({ ...newBlog, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Blog Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowModal(true)}>
          Add New Post
        </Button>
      </div>

      <Input.Search
        placeholder="Search posts..."
        enterButton={<SearchOutlined />}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 16, width: "50%" }}
      />

      <Table
        dataSource={filteredBlogs}
        rowKey="id"
        loading={loading}
        columns={[
          { title: "Title", dataIndex: "title", key: "title" },
          {
            title: "Content",
            dataIndex: "content",
            key: "content",
            render: (content) => <div dangerouslySetInnerHTML={{ __html: content }} />,
          },
          {
            title: "Image",
            dataIndex: "image",
            key: "image",
            render: (image) => (image ? <img src={`https://agpipefitting.org.et/storage/${image}`} alt="blog" width="100" /> : "No Image"),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => <Badge status={status === "Published" ? "success" : "default"} text={status} />,
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, blog) => (
              <Space>
                <Button icon={<EditOutlined />} onClick={() => handleEditBlog(blog)}>Edit</Button>
                <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteBlog(blog.id)}>Delete</Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal title={newBlog.id ? "Edit Blog" : "New Blog"} open={showModal} onCancel={handleCloseModal} onOk={handleAddBlog}>
        <Form layout="vertical">
          <Form.Item label="Title">
            <Input value={newBlog.title} onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })} />
          </Form.Item>
          <Form.Item label="Content">
            <ReactQuill value={newBlog.content} onChange={(content) => setNewBlog({ ...newBlog, content })} modules={quillModules} />
          </Form.Item>
          <Form.Item label="Status">
            <Select value={newBlog.status} onChange={(status) => setNewBlog({ ...newBlog, status })}>
              <Select.Option value="Draft">Draft</Select.Option>
              <Select.Option value="Published">Published</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Image">
            <input type="file" accept="image/*" onChange={handleImageChange} />
            {preview && <img src={preview} alt="Preview" width="100" className="mt-2" />}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogPage;
